import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Paper,
  Chip,
  Button,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';

function ComplianceProgress({ monthData, onGeneratePacket, onSignPacket, generating }) {
  const {
    receiptUploaded = 0,
    receiptTotal = 0,
    statementUploaded = 0,
    statementTotal = 0,
    needsAttentionCount = 0,
    status = 'open',
    statementPendingMsg = null,
  } = monthData || {};

  const receiptPct = receiptTotal > 0 ? (receiptUploaded / receiptTotal) * 100 : 0;
  const statementPct = statementTotal > 0 ? (statementUploaded / statementTotal) * 100 : 0;
  const isComplete = needsAttentionCount === 0 && receiptPct === 100 && statementPct === 100;

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">Progress</Typography>
        {isComplete ? (
          <Chip icon={<CheckCircleIcon />} label="Ready to Generate" color="success" />
        ) : (
          <Chip icon={<WarningIcon />} label={`${needsAttentionCount} items need attention`} color="warning" />
        )}
      </Box>

      {/* Receipt progress */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Receipts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {receiptUploaded}/{receiptTotal} uploaded
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={receiptPct}
          sx={{
            height: 10,
            borderRadius: 5,
            '& .MuiLinearProgress-bar': {
              backgroundColor: receiptPct === 100 ? 'success.main' : 'primary.main',
            },
          }}
        />
      </Box>

      {/* Statement progress */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Chase Statements
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {statementUploaded}/{statementTotal}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={statementPct}
          sx={{
            height: 10,
            borderRadius: 5,
            '& .MuiLinearProgress-bar': {
              backgroundColor: statementPct === 100 ? 'success.main' : 'primary.main',
            },
          }}
        />
        {statementPendingMsg && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
            {statementPendingMsg}
          </Typography>
        )}
      </Box>

      {/* Status text and generate button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {status === 'signed'
            ? 'Packet signed and complete'
            : status === 'complete'
            ? 'Packet generated — awaiting signature'
            : isComplete
            ? 'All items reviewed — ready to generate packet'
            : `${needsAttentionCount} items remaining`}
        </Typography>
        {(status === 'open' || status === 'complete') && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant={status === 'complete' ? 'outlined' : 'contained'}
              color="primary"
              disabled={!isComplete || generating}
              onClick={onGeneratePacket}
              size="small"
              startIcon={generating ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {generating ? 'Generating...' : status === 'complete' ? 'Regenerate' : 'Generate Packet'}
            </Button>
            {status === 'complete' && (
              <Button
                variant="contained"
                color="success"
                onClick={onSignPacket}
                size="small"
              >
                Sign & Finalize
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
}

export default ComplianceProgress;
